import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import {
  FiArrowUp,
  FiArrowDown,
  FiSearch,
  FiX,
  FiMail,
  FiUser,
  FiFileText,
  FiTag,
} from 'react-icons/fi';
import MoreMenu from '../components/MoreMenu';
import BulkActionBar from '../components/BulkActionBar';
import Modal from '../components/Modal';
import LoadingButton from '../components/LoadingButton';
import SendTemplateEmailModal from '../components/SendTemplateEmailModal';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { useClientPager } from '../hooks/useClientPager';
import ListPager from '../components/ListPager';
import { downloadCsv } from '../utils/exportCsv';
import { appAlert, appConfirm } from '../utils/appDialog';
import {
  REGISTER_STATUS_OPTIONS,
  DEFAULT_REGISTER_STATUS_FILTER,
  matchesRegisterStatusFilter,
  normalizeRegisterStatus,
  statusLabel,
  statusTone,
} from './registerShared';
import './Candidates.css';
import './RegisterCandidates.css';

export { REGISTER_STATUS_OPTIONS } from './registerShared';

const COLUMN_CONFIG = [
  { key: 'select', label: '', sortable: false },
  { key: 'sr', label: 'Sr No', sortable: false },
  { key: 'fname', label: 'First Name', sortable: true },
  { key: 'lname', label: 'Last Name', sortable: true },
  { key: 'email', label: 'Email', sortable: true },
  { key: 'phone', label: 'Phone', sortable: true },
  { key: 'status', label: 'Status', sortable: true },
  { key: 'pipeline', label: 'Pipeline', sortable: false },
  { key: 'position', label: 'Position', sortable: true },
  { key: 'service', label: 'Service', sortable: true },
  { key: 'resume', label: 'Resume', sortable: false },
  { key: 'start_date', label: 'Start Date', sortable: true },
  { key: 'price', label: 'Price', sortable: true },
  { key: 'ref', label: 'Ref', sortable: true },
  { key: 'actions', label: 'Actions', sortable: false },
];

const REGISTER_EXPORT_COLUMNS = [
  { key: 'id', label: 'ID' },
  { key: 'fname', label: 'First Name' },
  { key: 'lname', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  {
    key: 'status',
    label: 'Status',
    format: (row) => statusLabel(normalizeRegisterStatus(row.status)),
  },
  { key: 'position', label: 'Position' },
  { key: 'service', label: 'Service' },
  { key: 'start_date', label: 'Start Date' },
  { key: 'price', label: 'Price' },
  { key: 'ref', label: 'Ref' },
  {
    key: 'resume',
    label: 'Resume',
    format: (row) => row.resume_file_name || row.resume_r2_key || '',
  },
];

function toEmailRecipient(row) {
  return {
    registerCandidateId: row.id,
    email: row.email,
    firstName: row.fname,
    lastName: row.lname,
    displayName: [row.fname, row.lname].filter(Boolean).join(' ') || row.email,
  };
}

const RegisterCandidates = () => {
  const { user, loading } = useAuth();
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sortColumn, setSortColumn] = useState('createdon_timestamp');
  const [sortDirection, setSortDirection] = useState('desc');
  const [search, setSearch] = useState('');
  /** Default: active only (hides disabled/inactive). Removable via filter control. */
  const [statusFilter, setStatusFilter] = useState(DEFAULT_REGISTER_STATUS_FILTER);
  const [showSendEmail, setShowSendEmail] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkAction, setBulkAction] = useState(null);
  const [showBulkStatus, setShowBulkStatus] = useState(false);
  const [bulkStatusValue, setBulkStatusValue] = useState('interested');

  const canAccess =
    user?.role === 'admin' ||
    user?.role === 'consultant' ||
    hasPermission('tab_register');

  const { data: rows = [], isLoading, isError, error } = useQuery(
    ['register-candidates'],
    () => api.get('/register-candidates').then((res) => res.data),
    { refetchOnWindowFocus: false, staleTime: 15000, enabled: Boolean(canAccess && user) }
  );

  const filteredAndSorted = useMemo(() => {
    if (!rows || rows.length === 0) return [];

    const lowerSearch = search.trim().toLowerCase();

    let filtered = rows.filter((row) => {
      if (!matchesRegisterStatusFilter(row.status, statusFilter)) return false;
      if (!lowerSearch) return true;
      const hay = [
        row.fname,
        row.lname,
        row.email,
        row.phone,
        row.position,
        row.service,
        row.ref,
        row.private_notes,
        row.resume_file_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(lowerSearch);
    });

    const dir = sortDirection === 'asc' ? 1 : -1;
    filtered = [...filtered].sort((a, b) => {
      if (sortColumn === 'status') {
        const sa = statusLabel(normalizeRegisterStatus(a.status));
        const sb = statusLabel(normalizeRegisterStatus(b.status));
        return String(sa).localeCompare(String(sb)) * dir;
      }
      let va = a[sortColumn];
      let vb = b[sortColumn];
      if (sortColumn === 'price') {
        va = Number(va) || 0;
        vb = Number(vb) || 0;
        return (va - vb) * dir;
      }
      if (sortColumn === 'createdon_timestamp' || sortColumn === 'start_date') {
        const ta = Date.parse(va || 0) || 0;
        const tb = Date.parse(vb || 0) || 0;
        return (ta - tb) * dir;
      }
      return (
        String(va ?? '').localeCompare(String(vb ?? ''), undefined, {
          sensitivity: 'base',
        }) * dir
      );
    });

    return filtered;
  }, [rows, search, statusFilter, sortColumn, sortDirection]);

  const {
    pageItems: pagedRegisterRows,
    pagination: registerPagination,
    setPage: setRegisterPage,
  } = useClientPager(filteredAndSorted, {
    resetKey: `${search}|${statusFilter}|${sortColumn}|${sortDirection}`,
  });

  const {
    selectedItems,
    selectedCount,
    allSelected,
    someSelected,
    toggleOne,
    toggleAll,
    clearSelection,
    isSelected,
  } = useBulkSelection(pagedRegisterRows);

  useEffect(() => {
    clearSelection();
  }, [search, statusFilter, clearSelection]);

  const handleSort = (key) => {
    const col = COLUMN_CONFIG.find((c) => c.key === key);
    if (!col?.sortable) return;
    if (sortColumn === key) {
      setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(key);
      setSortDirection('asc');
    }
  };

  const openProfile = (row) => {
    if (!row?.id) return;
    navigate(`/register/${row.id}`);
  };

  const openSendEmail = (row, e) => {
    e?.stopPropagation?.();
    if (!row?.email) return;
    setEmailRecipients([toEmailRecipient(row)]);
    setShowSendEmail(true);
  };

  const openBulkEmail = () => {
    const withEmail = selectedItems.filter((r) => r.email);
    if (withEmail.length === 0) {
      void appAlert('None of the selected rows have an email address.');
      return;
    }
    if (withEmail.length < selectedItems.length) {
      void appAlert(
        `${selectedItems.length - withEmail.length} selected row(s) have no email and will be skipped.`
      );
    }
    setEmailRecipients(withEmail.map(toEmailRecipient));
    setShowSendEmail(true);
  };

  const handleBulkExport = () => {
    downloadCsv(
      selectedItems,
      REGISTER_EXPORT_COLUMNS,
      `register-export-${Date.now()}.csv`
    );
  };

  const openBulkStatus = () => {
    if (selectedCount < 1) return;
    const first = normalizeRegisterStatus(selectedItems[0]?.status);
    setBulkStatusValue(first || 'interested');
    setShowBulkStatus(true);
  };

  const applyBulkStatus = async () => {
    const status = normalizeRegisterStatus(bulkStatusValue);
    if (!await appConfirm(
      `Set status to “${statusLabel(status)}” for ${selectedCount} register row(s)?` +
        (status === 'created'
          ? '\n\nNote: this only updates register status; it does not create platform users.'
          : '')
    )) {
      return;
    }
    setBulkBusy(true);
    setBulkAction('status');
    try {
      const res = await api.post('/register-candidates/bulk-status', {
        ids: selectedItems.map((r) => r.id),
        status,
      });
      const data = res.data || {};
      queryClient.invalidateQueries(['register-candidates']);
      clearSelection();
      setShowBulkStatus(false);
      void appAlert(
        `Updated ${data.updated_count ?? 0}` +
          (data.skipped_count ? `, skipped ${data.skipped_count} already at that status` : '') +
          (data.not_found_count ? `, ${data.not_found_count} not found` : '') +
          '.'
      );
    } catch (e) {
      void appAlert(e.response?.data?.error || e.message);
    } finally {
      setBulkBusy(false);
      setBulkAction(null);
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!canAccess) {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return <div className="loading">Loading register candidates...</div>;
  }

  if (isError) {
    return (
      <div className="loading">
        Failed to load register candidates: {error?.response?.data?.error || error?.message}
      </div>
    );
  }

  const hasSearch = Boolean(search.trim());

  return (
    <div className="candidates-page list-page register-page">
      <div className={`page-header${selectedCount > 0 ? ' page-header--bulk' : ''}`}>
        {selectedCount > 0 ? (
          <BulkActionBar
            className="bulk-action-bar--header"
            count={selectedCount}
            onClear={clearSelection}
            onExport={handleBulkExport}
            showDelete={false}
            showMatch={false}
            busy={bulkBusy}
            busyAction={bulkAction}
            actions={[
              {
                key: 'email',
                icon: FiMail,
                label: 'Send email to selected',
                onClick: openBulkEmail,
              },
              {
                key: 'status',
                icon: FiTag,
                label: 'Update status',
                onClick: openBulkStatus,
              },
            ]}
          />
        ) : (
          <div>
            <h1>Register</h1>
            <p className="page-subtitle">
              Intake profiles from external registration. Disabled users are hidden by
              default — clear the status chip or choose “All statuses” to include them.
              Select rows for bulk email or status updates; open a row for profile, resume,
              and history.
            </p>
          </div>
        )}
      </div>

      <div className="list-filters-panel list-toolbar-panel">
        <div className="filter-row list-toolbar-row register-toolbar">
          <div className="filter-group filter-group--wide list-toolbar-search">
            <div className="list-filter-input-icon list-search-bar__input">
              <FiSearch aria-hidden />
              <input
                id="register-filter-search"
                type="search"
                placeholder="Search name, email, notes, ref…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoComplete="off"
              />
              {hasSearch && (
                <button
                  type="button"
                  className="list-search-bar__clear"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                >
                  <FiX aria-hidden />
                </button>
              )}
            </div>
          </div>
          <div className="filter-group register-status-filter-group">
            <label htmlFor="register-status-filter" className="visually-hidden">
              Status filter
            </label>
            <select
              id="register-status-filter"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label="Status filter"
            >
              <option value="active">Active (hide disabled)</option>
              <option value="all">All statuses</option>
              {REGISTER_STATUS_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
            {statusFilter === 'active' && (
              <button
                type="button"
                className="register-filter-chip"
                onClick={() => setStatusFilter('all')}
                title="Show disabled and inactive rows too"
              >
                Hiding disabled
                <FiX aria-hidden />
                <span className="visually-hidden"> — clear filter, show all statuses</span>
              </button>
            )}
            {statusFilter !== 'active' && statusFilter !== 'all' && (
              <button
                type="button"
                className="register-filter-chip register-filter-chip--custom"
                onClick={() => setStatusFilter(DEFAULT_REGISTER_STATUS_FILTER)}
                title="Restore default active filter"
              >
                {statusLabel(statusFilter)}
                <FiX aria-hidden />
                <span className="visually-hidden"> — reset to active filter</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="candidates-table-container">
        {filteredAndSorted.length === 0 ? (
          <div className="empty-state">No register records match your filters</div>
        ) : (
          <table
            className="table candidates-table"
            style={{ tableLayout: 'auto', minWidth: '980px' }}
          >
            <thead>
              <tr>
                {COLUMN_CONFIG.map((col) => {
                  if (col.key === 'select') {
                    return (
                      <th key="select" className="list-select-cell">
                        <input
                          type="checkbox"
                          aria-label="Select all register rows"
                          checked={allSelected && pagedRegisterRows.length > 0}
                          ref={(el) => {
                            if (el) el.indeterminate = someSelected && !allSelected;
                          }}
                          onChange={toggleAll}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </th>
                    );
                  }
                  return (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      style={{
                        cursor: col.sortable ? 'pointer' : 'default',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        {col.label}
                        {col.sortable && sortColumn === col.key && (
                          sortDirection === 'asc' ? (
                            <FiArrowUp style={{ fontSize: 14 }} />
                          ) : (
                            <FiArrowDown style={{ fontSize: 14 }} />
                          )
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {pagedRegisterRows.map((row, index) => {
                const st = normalizeRegisterStatus(row.status);
                const hasResume = Boolean(row.has_resume || row.resume_r2_key || row.resume_url);
                const selected = isSelected(row.id);
                return (
                  <tr
                    key={row.id || `${row.email}-${index}`}
                    className={`register-row-clickable${selected ? ' row-selected' : ''}`}
                    onClick={() => openProfile(row)}
                  >
                    {COLUMN_CONFIG.map((col) => {
                      if (col.key === 'select') {
                        return (
                          <td
                            key="select"
                            className="list-select-cell"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              aria-label={`Select ${row.email || row.id}`}
                              checked={selected}
                              onChange={() => toggleOne(row.id)}
                            />
                          </td>
                        );
                      }
                      if (col.key === 'sr') {
                        return <td key="sr">{registerPagination.offset + index + 1}</td>;
                      }
                      if (col.key === 'status') {
                        return (
                          <td key="status">
                            <span
                              className={`register-status-badge register-status-badge--${statusTone(st)}`}
                            >
                              {statusLabel(st)}
                            </span>
                          </td>
                        );
                      }
                      if (col.key === 'pipeline') {
                        const steps = row.onboarding?.steps || [];
                        const current =
                          steps.find((s) => s.current) ||
                          steps.find((s) => !['done', 'sent', 'created'].includes(s.state)) ||
                          steps[steps.length - 1];
                        const label = current
                          ? `${current.label}: ${current.state}`
                          : '—';
                        return (
                          <td key="pipeline">
                            <span className="register-pipeline-chip" title={label}>
                              {current?.label || '—'}
                              {current?.state ? (
                                <em> · {current.state}</em>
                              ) : null}
                            </span>
                          </td>
                        );
                      }
                      if (col.key === 'resume') {
                        return (
                          <td key="resume">
                            {hasResume ? (
                              <span
                                className="register-resume-chip"
                                title={row.resume_file_name || 'Resume'}
                              >
                                <FiFileText aria-hidden /> {row.resume_file_name || 'Attached'}
                              </span>
                            ) : (
                              <span className="form-hint">—</span>
                            )}
                          </td>
                        );
                      }
                      if (col.key === 'actions') {
                        return (
                          <td key="actions" onClick={(e) => e.stopPropagation()}>
                            <div className="register-row-actions">
                              <MoreMenu
                                label={`Actions for ${row.first_name || ''} ${row.last_name || ''}`.trim() || 'candidate'}
                                size="sm"
                                items={[
                                  {
                                    id: 'profile',
                                    label: 'Open profile',
                                    icon: FiUser,
                                    onSelect: () => openProfile(row),
                                  },
                                  {
                                    id: 'email',
                                    label: 'Send email',
                                    icon: FiMail,
                                    hidden: !row.email,
                                    onSelect: () => openSendEmail(row),
                                  },
                                ]}
                              />
                            </div>
                          </td>
                        );
                      }

                      let value = row[col.key];
                      if (col.key === 'start_date' && value) {
                        try {
                          const d = new Date(value);
                          if (!isNaN(d.getTime())) value = d.toISOString().slice(0, 10);
                        } catch {
                          /* keep */
                        }
                      }

                      return (
                        <td key={col.key}>
                          {value === null || typeof value === 'undefined' ? '' : String(value)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ListPager
        page={registerPagination.page}
        pageCount={registerPagination.pageCount}
        total={registerPagination.total}
        limit={registerPagination.limit}
        onPageChange={setRegisterPage}
      />

      <SendTemplateEmailModal
        open={showSendEmail && emailRecipients.length > 0}
        onClose={() => {
          setShowSendEmail(false);
          setEmailRecipients([]);
        }}
        recipients={emailRecipients}
        onSent={() => {
          clearSelection();
          queryClient.invalidateQueries(['register-candidates']);
        }}
      />

      <Modal
        open={showBulkStatus}
        onClose={() => !bulkBusy && setShowBulkStatus(false)}
        ariaLabel="Bulk update status"
        contentClassName="register-bulk-status-modal"
      >
        <div className="register-bulk-status-body">
          <h2>Update status</h2>
          <p className="form-hint">
            Apply one lifecycle status to <strong>{selectedCount}</strong> selected row(s).
          </p>
          <div className="form-group">
            <label htmlFor="register-bulk-status">Status</label>
            <select
              id="register-bulk-status"
              value={bulkStatusValue}
              onChange={(e) => setBulkStatusValue(e.target.value)}
              disabled={bulkBusy}
            >
              {REGISTER_STATUS_OPTIONS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                  {s.hint ? ` — ${s.hint}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setShowBulkStatus(false)}
              disabled={bulkBusy}
            >
              Cancel
            </button>
            <LoadingButton
              type="button"
              className="btn btn-primary"
              loading={bulkBusy && bulkAction === 'status'}
              loadingLabel="Updating…"
              onClick={applyBulkStatus}
            >
              Update {selectedCount}
            </LoadingButton>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default RegisterCandidates;
